// File: frontend/src/components/entries/UnifiedRefPopup.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { quickSearch, searchCategory } from "../../services/searchService";
import {
  QuickSearchResult,
  FullSearchResult,
  SearchMatchResult,
  SearchPersonResult,
  SearchClubResult,
  SearchTeamResult,
  SearchCustomTopicResult,
} from "../../models/SearchModel";
import { RefInsertMode, RefModeToggle, applyRefMode } from "./EntryEditorToolbar";
import { toLowerTr, stripDisambiguationSuffix } from "../../utils/entryContentUtils";
import "./UnifiedRefPopup.css";

/**
 * One reference picker for all five topic domains.
 *
 * It replaces five separate popups — and five separate toolbar buttons — that
 * differed only in which endpoint they searched and which keyword they wrote
 * into the token. Asking the writer to first decide "is this a club or a team
 * or a person?" and only then type the name inverts how people actually think:
 * they know the name, not its category. Here they type once and every domain
 * answers, grouped under headings so the categories are still visible in the
 * result — the distinction is shown rather than demanded up front.
 *
 * All five token formats are unchanged, so existing entries and everything
 * that parses them keep working:
 *   (baslik: label||id)  (mac: label||id)  (kulup: label||id)
 *   (takim: label||id)   (kisi: label||id)
 * The bkz variant is applied exactly as before via applyRefMode.
 */

interface UnifiedRefPopupProps {
  onSelect: (text: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
  /** Text selected in the editor when the button was pressed. */
  initialQuery?: string;
}

/** Minimum characters before searching, matching the previous popups. */
const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

/** Per-category cap so no single domain crowds the others out of the list. */
const PER_CATEGORY_LIMIT = 5;

type RefCategory = "baslik" | "mac" | "kulup" | "takim" | "kisi";

/**
 * A search hit flattened into what this popup needs: what to display, and what
 * token to write. Keeping the token decision next to the label keeps the five
 * formats in one place instead of scattered across five components.
 */
interface RefCandidate {
  category: RefCategory;
  id: number;
  /** Main line in the list. */
  label: string;
  /** Match year, used by the year filter. Only set for matches. */
  year?: number;
  /** Text used inside the token — usually the label without its context. */
  tokenLabel: string;
}

const CATEGORY_ORDER: RefCategory[] = ["baslik", "mac", "kulup", "takim", "kisi"];

/**
 * Backend category names, as used by /api/search/category. Selecting one
 * category switches to that endpoint so the writer can page past the handful
 * of hits quick search returns per domain — which matters most for matches,
 * where a common club name can legitimately have hundreds.
 */
const CATEGORY_ENDPOINT: Record<RefCategory, keyof FullSearchResult> = {
  baslik: "customTopics",
  mac: "matches",
  kulup: "clubs",
  takim: "teams",
  kisi: "people",
};

/** Page size when a single category is selected and paged through. */
const CATEGORY_PAGE_SIZE = 20;

/** Years offered when narrowing matches — current year back to 2020. */
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - 2019 },
  (_, i) => String(CURRENT_YEAR - i)
);

const CATEGORY_HEADINGS: Record<RefCategory, string> = {
  baslik: "başlıklar",
  mac: "maçlar",
  kulup: "kulüpler",
  takim: "takımlar",
  // "camia" is what this domain is called in the tab bar; the picker said
  // "kişiler", so the same set of things had two names in one product.
  kisi: "camia",
};

const TURKISH_MONTHS = [
  "ocak", "şubat", "mart", "nisan", "mayıs", "haziran",
  "temmuz", "ağustos", "eylül", "ekim", "kasım", "aralık",
];

/** "14 mart 2026 fenerbahçe galatasaray maçı" — same shape the match popup used. */
const buildMatchLabel = (match: SearchMatchResult): string => {
  const date = new Date(match.matchDate);
  const day = date.getDate();
  const month = TURKISH_MONTHS[date.getMonth()];
  const year = date.getFullYear();
  const home = match.homeTeamTr ?? match.homeTeam;
  const away = match.awayTeamTr ?? match.awayTeam;
  return `${day} ${month} ${year} ${home} ${away} maçı`;
};

/**
 * Turkish sport names, keyed by the backend enum.
 *
 * The enum arrives raw ("FOOTBALL", "ICE_HOCKEY"), and simply lowercasing it
 * is what put "fenerbahçe football" into entries. This popup cannot use the
 * i18n "sport_type_*" keys the way the old team picker did, because it builds
 * labels in a module-level helper with no access to t(); the mapping is
 * therefore spelled out here, matching those keys.
 */
const SPORT_LABELS_TR: Record<string, string> = {
  FOOTBALL: "futbol",
  BASKETBALL: "basketbol",
  VOLLEYBALL: "voleybol",
  AMERICAN_FOOTBALL: "amerikan futbolu",
  BASEBALL: "beyzbol",
  ICE_HOCKEY: "buz hokeyi",
  HANDBALL: "hentbol",
};

/**
 * "fenerbahçe futbol takımı", "galatasaray voleybol kadın takımı",
 * "fenerbahçe beko basketbol takımı".
 *
 * Restores the shape the team-only picker produced before the five popups were
 * merged. The merge had reduced this to the club name plus a raw enum, which
 * both dropped the "takımı" suffix and left the sport untranslated — so a team
 * reference no longer read as a team at all.
 */
const buildTeamLabel = (team: SearchTeamResult): string => {
  // displayName ("Fenerbahçe Beko") replaces the club name but not the rest of
  // the label: it names the team's sponsor identity, not its sport.
  const base = team.displayName ?? team.nameTr ?? team.name;
  const parts = [base];
  // Qualifiers come BEFORE the sport, which is how the name is said out loud:
  // "fenerbahçe u19 futbol takımı", not "fenerbahçe futbol u19 takımı". The
  // sport is the head noun of the phrase, so everything narrowing it precedes
  // it and "takımı" closes it.
  if (team.teamCode) parts.push(team.teamCode.toUpperCase());
  if (team.gender === "FEMALE") parts.push("kadın");
  else if (team.gender === "MIXED") parts.push("karma");
  if (team.sportType) {
    parts.push(SPORT_LABELS_TR[team.sportType] ?? team.sportType.toLowerCase());
  }
  parts.push("takımı");
  return parts.join(" ");
};

const buildPersonLabel = (person: SearchPersonResult): string =>
  person.knownName ?? `${person.firstName} ${person.lastName}`.trim();

/**
 * Flattens a quick-search response into one ordered candidate list.
 * `limit` caps each category — a few per domain while all five share the
 * screen, effectively unlimited when paging through a single selected one.
 */
const toCandidates = (result: QuickSearchResult, limit: number = PER_CATEGORY_LIMIT): RefCandidate[] => {
  const out: RefCandidate[] = [];

  (result.customTopics ?? []).slice(0, limit).forEach((topic: SearchCustomTopicResult) => {
    out.push({
      category: "baslik",
      id: topic.id,
      label: topic.title,
      tokenLabel: topic.title,
    });
  });

  (result.matches ?? []).slice(0, limit).forEach((match: SearchMatchResult) => {
    const label = buildMatchLabel(match);
    out.push({
      category: "mac",
      id: match.id,
      label,
      // No league line: the label already carries date and both teams, which
      // is everything needed to pick the right fixture. The competition only
      // added a second row to every result.
      year: new Date(match.matchDate).getFullYear(),
      tokenLabel: label,
    });
  });

  (result.clubs ?? []).slice(0, limit).forEach((club: SearchClubResult) => {
    const label = club.nameTr ?? club.shortName;
    out.push({ category: "kulup", id: club.clubId, label, tokenLabel: label });
  });

  (result.teams ?? []).slice(0, limit).forEach((team: SearchTeamResult) => {
    const label = buildTeamLabel(team);
    out.push({ category: "takim", id: team.teamId, label, tokenLabel: label });
  });

  (result.people ?? []).slice(0, limit).forEach((person: SearchPersonResult) => {
    const label = buildPersonLabel(person);
    // No second line. Every candidate is now a single row across all five
    // domains: the category heading above the group already says what kind of
    // thing this is, and the extra line ("eğitim", a club name) doubled the
    // height of every hit for context nobody was choosing on. On a phone that
    // halved how many results fit on screen.
    out.push({ category: "kisi", id: person.id, label, tokenLabel: label });
  });

  return out;
};

export const UnifiedRefPopup: React.FC<UnifiedRefPopupProps> = ({
  onSelect,
  onClose,
  style,
  initialQuery = "",
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [candidates, setCandidates] = useState<RefCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [refMode, setRefMode] = useState<RefInsertMode>("inline");
  // At most one category at a time. Multi-select was worse than useless here:
  // combining, say, matches and clubs produces the same mixed list the writer
  // already sees with nothing selected. One category means one clear question
  // — "show me only matches" — and it is what makes paging past the first
  // handful of results possible below.
  // Opens on "başlıklar". Showing all five stacked was the wrong default: most
  // references are to gündem topics, and five headings at once turns a picker
  // into a wall. The other four are one tap away.
  const [activeCategory, setActiveCategory] = useState<RefCategory | null>("baslik");

  // Extra results loaded for the selected category, appended after the ones
  // quick search already returned.
  const [categoryExtras, setCategoryExtras] = useState<RefCandidate[]>([]);
  const [categoryPage, setCategoryPage] = useState(0);
  const [categoryHasMore, setCategoryHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Year narrowing for matches. A club plays hundreds of them, and the name
  // alone cannot separate this season's fixture from one eight years ago.
  // Appended to the query text, which is how the old match-only popup did it,
  // so the backend needs no new parameter.
  const [selectedYear, setSelectedYear] = useState<string>("");

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // The year is NOT appended to the search text. Doing that made the backend
  // treat "2026" as another word to match against team and competition names,
  // so a query that had results returned none. It narrows the results here
  // instead, against each match's own date.


  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setCandidates([]);
      setError("");
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError("");
      try {
        const result = await quickSearch(trimmed);
        setCandidates(toCandidates(result));
      } catch {
        setError(t("search_error"));
      } finally {
        setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, t]);

  const selectCategory = useCallback((category: RefCategory) => {
    // Paging state is reset by the page-0 effect below, which reruns whenever
    // the active category or the query changes.
    setActiveCategory((prev) => (prev === category ? null : category));
    setSelectedYear("");
  }, []);

  /**
   * Loads page 0 of the selected category straight from the category endpoint.
   *
   * Quick search caps every domain at PER_CATEGORY_LIMIT and says nothing
   * about whether it hit that cap, so "5 results" and "5 results and more
   * behind them" look identical. Guessing from the count is what produced a
   * "daha fazla göster" button that did nothing when a category happened to
   * have exactly five hits. The category endpoint returns hasMore, so the
   * button appears only when there is genuinely a next page.
   */
  useEffect(() => {
    setCategoryExtras([]);
    setCategoryPage(0);
    setCategoryHasMore(false);

    const trimmed = query.trim();
    if (!activeCategory || trimmed.length < MIN_QUERY_LENGTH) return;

    let cancelled = false;
    const field = CATEGORY_ENDPOINT[activeCategory];

    searchCategory(trimmed, field, 0, CATEGORY_PAGE_SIZE)
      .then((data) => {
        if (cancelled) return;
        const page = data[field];
        if (!page) return;
        const asQuick = { [field]: page.content } as unknown as QuickSearchResult;
        setCategoryExtras(toCandidates(asQuick, Number.MAX_SAFE_INTEGER));
        setCategoryHasMore(page.hasMore);
      })
      .catch(() => {
        // Quick search results stay on screen; only paging is unavailable.
        if (!cancelled) setCategoryHasMore(false);
      });

    return () => { cancelled = true; };
  }, [activeCategory, query]);

  /**
   * Fetches the next page for the selected category, appending to what page 0
   * already loaded. Matters most for matches, where a common club name can
   * legitimately have hundreds.
   */
  const loadMoreInCategory = useCallback(async () => {
    if (!activeCategory) return;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return;

    setIsLoadingMore(true);
    try {
      const field = CATEGORY_ENDPOINT[activeCategory];
      const nextPage = categoryPage + 1;
      const data = await searchCategory(trimmed, field, nextPage, CATEGORY_PAGE_SIZE);
      const page = data[field];
      if (!page) {
        setCategoryHasMore(false);
        return;
      }
      // Reuse the same flattening as quick search by handing it a response
      // shaped like one: only the selected category is populated.
      const asQuick = { [field]: page.content } as unknown as QuickSearchResult;
      setCategoryExtras((prev) => [...prev, ...toCandidates(asQuick, Number.MAX_SAFE_INTEGER)]);
      setCategoryPage(nextPage);
      setCategoryHasMore(page.hasMore);
    } catch {
      setCategoryHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [activeCategory, query, categoryPage]);

  const handleSelect = useCallback((candidate: RefCandidate) => {
    // Identical to what the five separate popups produced: the disambiguation
    // suffix is stripped from the visible label, the label is lowercased per
    // sözlük convention, and the id tail is left untouched so navigation and
    // later renames keep working.
    const label = toLowerTr(stripDisambiguationSuffix(candidate.tokenLabel));
    onSelect(`(${applyRefMode(candidate.category, refMode)}: ${label}||${candidate.id})`);
    onClose();
  }, [onSelect, onClose, refMode]);

  const base = activeCategory === null
    ? candidates
    : candidates.filter((c) => c.category === activeCategory);

  // Extras can repeat what quick search already returned, so they are keyed out
  // rather than shown twice.
  const seen = new Set(base.map((c) => `${c.category}-${c.id}`));
  const combined = [...base, ...categoryExtras.filter((c) => !seen.has(`${c.category}-${c.id}`))];
  const visible = selectedYear
    ? combined.filter((c) => c.category !== "mac" || String(c.year) === selectedYear)
    : combined;

  // How many domains actually have results on screen. Drives whether group
  // headings are drawn at all.
  const groupedCategoryCount = CATEGORY_ORDER
    .filter((category) => visible.some((c) => c.category === category)).length;

  // hasMore comes from the category endpoint, so the button is shown only when
  // a next page actually exists.
  const canLoadMore = activeCategory !== null && categoryHasMore && visible.length > 0;

  const hasInput = query.trim().length >= MIN_QUERY_LENGTH;

  return (
    <div className="ref-popup unified-ref-popup" style={style}>
      <div className="ref-popup-header">
        <RefModeToggle mode={refMode} onChange={setRefMode} />
      </div>

      <input
        ref={inputRef}
        className="ref-popup-input"
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="başlık, maç, kulüp, takım ya da camia ara..."
      />

      {/* Category filters. Optional by design — they narrow results that are
          already on screen, so the writer never has to choose a domain before
          searching. */}
      <div className="unified-ref-popup__filters">
        {CATEGORY_ORDER.map((category) => {
          const isActive = activeCategory === category;
          const count = candidates.filter((c) => c.category === category).length;
          return (
            <button
              key={category}
              type="button"
              className={`unified-ref-popup__filter${isActive ? " unified-ref-popup__filter--active" : ""}`}
              onClick={() => selectCategory(category)}
              disabled={count === 0}
            >
              {CATEGORY_HEADINGS[category]}
              {count > 0 && <span className="unified-ref-popup__filter-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Year narrowing, shown only where it means something. Every other
          domain is a single entity that a name already pins down. */}
      {activeCategory === "mac" && (
        <div className="unified-ref-popup__year-row">
          <select
            className="unified-ref-popup__year"
            value={selectedYear}
            onChange={(e) => {
              // Narrowing happens client-side against each match's own date,
              // so the loaded pages stay valid and are simply filtered.
              setSelectedYear(e.target.value);
            }}
          >
            <option value="">tüm yıllar</option>
            {YEAR_OPTIONS.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      )}

      <div className="ref-popup-results">
        {isLoading && <div className="ref-popup-status">{t("search_loading", "aranıyor...")}</div>}
        {!isLoading && error && <div className="ref-popup-status ref-popup-status--error">{error}</div>}
        {!isLoading && !error && !hasInput && (
          <div className="ref-popup-status">
            {t("ref_popup_hint", "aramak için en az iki harf yazınız.")}
          </div>
        )}
        {!isLoading && !error && hasInput && visible.length === 0 && (
          <div className="ref-popup-status">{t("search_no_results", "sonuç bulunamadı.")}</div>
        )}

        {!isLoading && !error && CATEGORY_ORDER.map((category) => {
          const group = visible.filter((c) => c.category === category);
          if (group.length === 0) return null;
          return (
            <div key={category} className="unified-ref-popup__group">
              {/* A heading only earns its row when it separates one group from
                  another. When a single group has results — because a filter
                  is active, or because only one domain matched the query — it
                  just repeats the chip sitting directly above it. Keying this
                  off the filter alone was not enough: with no filter active
                  and one matching domain, the word still appeared twice. */}
              {groupedCategoryCount > 1 && (
                <div className="unified-ref-popup__group-heading">{CATEGORY_HEADINGS[category]}</div>
              )}
              {group.map((candidate) => (
                <button
                  key={`${candidate.category}-${candidate.id}`}
                  type="button"
                  className="unified-ref-popup__result"
                  onClick={() => handleSelect(candidate)}
                >
                  <span className="unified-ref-popup__result-label">{candidate.label}</span>
                </button>
              ))}
            </div>
          );
        })}

      </div>

      {/* Paging is offered only inside a selected category: with all five on
          screen a "more" button would be ambiguous about what it loads.
          The button used to appear whenever nothing had been paged yet, which
          offered "daha fazla göster" on a handful of results and then loaded
          nothing when tapped. It now follows the backend's hasMore flag. */}
      {!isLoading && !error && activeCategory !== null && canLoadMore && (
        <button
          type="button"
          className="unified-ref-popup__more"
          onClick={loadMoreInCategory}
          disabled={isLoadingMore}
        >
          {isLoadingMore
            ? t("search_loading", "aranıyor...")
            : t("ref_popup_more", "daha fazla göster")}
        </button>
      )}
    </div>
  );
};